import { useState, useEffect, useRef } from 'react';
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { accessLevels } from '../constants';
import { devLog } from '../utils/devLog';

export function useAuth({ onSignedOut }) {
  const [loginId, setLoginId] = useState('');
  const [userGroups, setUserGroups] = useState([]);
  const [accessLevel, setAccessLevel] = useState(accessLevels.guest);
  const justSignedOutRef = useRef(false);

  async function doGetTheSignedInUser() {
    var theSignedInDetails = null;
    try {
      // await fetchAuthSession({ forceRefresh: true });
      const { signInDetails } = await getCurrentUser();
      if (signInDetails === undefined) {
        await signOut();
      } else {
        setLoginId(signInDetails.loginId);
        // signInDetails carries the account's login identity (email, in a
        // Cognito email-based pool) — don't log it verbatim.
        devLog('Got the signed in user');
        theSignedInDetails = signInDetails;
      }
    } catch (err) {
      devLog(err);
    }
    return theSignedInDetails;
  }

  useEffect(() => {
    devLog('Hub Listen  changed.');
    const removeAuthListener = Hub.listen('auth', (data) => {
      const { payload } = data;
      if (payload.event === 'signedIn') {
        try {
          // payload.data.signInDetails carries the account's login identity
          // (email, in a Cognito email-based pool) — don't log it verbatim.
          devLog('Signin event received');
          setLoginId(payload.data.signInDetails.loginId);
        } catch (error) {
          devLog('signIn:', error);
        }
      } else if (payload.event === 'signedOut') {
        justSignedOutRef.current = true;
        setLoginId('');
        setUserGroups([]);
        setAccessLevel(accessLevels.guest);
        onSignedOut?.();
      }
    });

    return () => {
      removeAuthListener();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // loginId is the account's login identity (email, in a Cognito
    // email-based pool) — log only whether a session is present, not the
    // identity itself.
    devLog('loginId: changed, now', loginId.length !== 0 ? 'set' : 'empty');

    if (loginId.length !== 0) {
      async function currentSession() {
        try {
          devLog('Fetching current session...');
          const session = await fetchAuthSession();
          const { accessToken } = session.tokens ?? {};
          const groups = accessToken?.payload?.['cognito:groups'];

          if (groups !== undefined && groups !== null && groups.length > 0) {
            devLog('User Groups:', groups);
            setUserGroups(groups);
          } else {
            setUserGroups([]);
          }

          if (session.credentials) {
            devLog(
              'Current AWS Role ARN:',
              session.credentials.authenticatedRoleArn || session.credentials.unauthenticatedRoleArn,
            );
            devLog('Identity ID:', session.identityId);
          } else {
            devLog('No credentials found.');
          }
        } catch (err) {
          devLog('Fetching current session', err);
          setUserGroups([]);
        }
      }
      currentSession();
    } else if (justSignedOutRef.current) {
      // Don't re-check for a signed-in user immediately after a deliberate
      // sign-out set loginId back to '' — that would race sign-out completion.
      justSignedOutRef.current = false;
    } else {
      doGetTheSignedInUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginId]);

  return { loginId, userGroups, accessLevel, setAccessLevel };
}
